/**
 * SettingsListingTable Component
 *
 * This component renders a table for displaying settings data, such as user or location information.
 * It supports sorting, displaying modals for detailed information, and editing entries. The component
 * uses React Bootstrap for styling and modals, and Ant Design icons for visual elements.
 *
 * Props:
 * - tableHeaders: An object representing the headers of the table.
 * - tableData: An array of data objects to be displayed in the table.
 * - tableType: A string indicating the type of table ("location" or "user").
 * - fetchUsersAndLocations: A function to fetch users and locations data.
 * - isUserSuperAdmin: A boolean indicating if the current user is a super admin.
 *
 * Example usage:
 * <SettingsListingTable
 *   tableHeaders={tableHeaders}
 *   tableData={tableData}
 *   tableType="user"
 *   fetchUsersAndLocations={fetchUsersAndLocations}
 *   isUserSuperAdmin={true}
 * />
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import SettingsLocationMoreInfoModal from "../Modal/SettingsLocationMoreInfoModal";
import SettingsUserMoreInfoModal from "../Modal/SettingsUserMoreInfoModal";
import {
  InfoCircleOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from "../../../../helpers/icons/antDesignIcons";
import "./SettingsListingTable.scss";
import useTranslation from "../../../../hooks/useTranslation";
import SettingsCompanyMoreInfoModal from "../Modal/SettingsCompanyMoreInfoModal";

const SettingsListingTable = ({ tableHeaders, tableData, tableType }) => {
  const navigate = useNavigate();
  const { t } = useTranslation("settingsPage");
  // User info modal state
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [modalUserInfo, setModalUserInfo] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  // Location info modal state
  const [showLocationInfoModal, setShowLocationInfoModal] = useState(false);
  const [modalLocationInfo, setModalLocationInfo] = useState("");
  // Company info modal state
  const [showCompanyInfoModal, setShowCompanyInfoModal] = useState(false);
  const [modalCompanyInfo, setModalCompanyInfo] = useState("");

  // Notification info modal state
  const [showNotificationInfoModal, setShowNotificationInfoModal] =
    useState(false);
  // Sort state
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  const roleMapping = {
    0: t("SUPER_ADMIN"),
    1: t("COMPANY_ADMIN"),
    2: t("COMPANY_LOCATION_LEADER"),
    /*     3: "COMPANY_LOCATION_LEADER", */
    4: t("COMPANYUSER"),
    5: t("RESELLER"),
  };

  // Handle user info modal
  const handleUserInfoModalClose = () => setShowUserInfoModal(false);
  const handleUserInfoModalShow = (userInfo) => {
    setModalUserInfo(userInfo);
    setShowUserInfoModal(true);
  };

  // Handle location info modal
  const handleLocationInfoModalClose = () => setShowLocationInfoModal(false);
  const handleLocationInfoModalShow = (locationInfo) => {
    console.log("handleLocationInfoModalShow", locationInfo);
    setModalLocationInfo(locationInfo);
    setShowLocationInfoModal(true);
  };

  // Handle company info modal
  const handleCompanyInfoModalClose = () => setShowCompanyInfoModal(false);

  const handleCompanyInfoModalShow = (companyInfo) => {
    setModalCompanyInfo(companyInfo);
    setShowCompanyInfoModal(true);
  };

  // Handle notification info modal
  const handleNotificationInfoModalClose = () =>
    setShowNotificationInfoModal(false);
  const handleNotificationInfoModalShow = () =>
    setShowNotificationInfoModal(true);

  const handleEditUser = (modalUserInfo) => {
    navigate(`/settings/company/edit-user/${modalUserInfo._id}`);
  };

  const handleEditLocation = (location) => {
    setSelectedLocation(location);
    navigate(`/settings/company/sites/edit-location/${location._id}`);
  };

  const handleEditCompany = (company) => {
    navigate(`/settings/company/edit-company/${company.companyid}`);
  };

  // Handle sorting
  const handleSort = (field) => {
    let direction = "asc";
    if (sortField === field && sortDirection === "asc") {
      direction = "desc";
    }
    setSortField(field);
    setSortDirection(direction);
  };

  // Sort the data
  const sortedData = Array.isArray(tableData)
    ? [...tableData].sort((a, b) => {
        // Existing sorting logic for other table types
        if (sortField) {
          if (sortDirection === "asc") {
            if (a[sortField] === b[sortField]) {
              return a.firstname.localeCompare(b.firstname);
            }
            return a[sortField].localeCompare(b[sortField]);
          } else {
            if (a[sortField] === b[sortField]) {
              return b.firstname.localeCompare(a.firstname);
            }
            return b[sortField].localeCompare(a[sortField]);
          }
        }
        return 0;
      })
    : [];

  return (
    <>
      {/* -------- TABLE TYPE: LOCATION -------- */}
      {tableType === "location" && (
        <div className="settings-listing-table table-wrapper">
          <Table>
            <thead className="sticky-table-header">
              <tr className="table-header-tr">
                {Object.keys(tableHeaders).map((key, index) => (
                  <th
                    key={index}
                    /*  className={`table-header__item ${key}`} */
                    id={`csl-${key}-header`}
                  >
                    <p className="table-header__title">{tableHeaders[key]}</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(!tableData || tableData.length === 0) && (
                <tr>
                  <td
                    colSpan={Object.keys(tableHeaders).length}
                    className="table-data"
                  >
                    {t("noDataToDisplay")}
                  </td>
                </tr>
              )}
              {tableData &&
                tableData.map((data, index) => (
                  <tr key={index}>
                    {Object.keys(tableHeaders).map((key, index) => (
                      <td
                        key={index}
                        /*  className={`table-data ${key}`} */
                        id={`csl-${key}-data`}
                      >
                        <div className="ellipsis">
                          {key === "locationMoreInfo" ? (
                            // Only render the button if data is valid
                            <Button
                              variant="link"
                              onClick={() => {
                                handleLocationInfoModalShow(data);
                              }}
                            >
                              <InfoCircleOutlined />
                            </Button>
                          ) : key === "sitecity" ? (
                            // Check if sitecity exists and is non-empty, otherwise display fallback message
                            data?.sitecity && data.sitecity.length > 0 ? (
                              `${data.sitecity}`
                            ) : (
                              `${t("noDataToDisplay")}`
                            )
                          ) : data && data[key] ? (
                            // General case for other keys in data
                            data[key]
                          ) : (
                            // Fallback for missing or empty data
                            t("noData")
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
            <SettingsLocationMoreInfoModal
              show={showLocationInfoModal}
              onClose={handleLocationInfoModalClose}
              locationInfo={modalLocationInfo}
              onEditLocation={handleEditLocation}
              t={t}
            />
          </Table>
        </div>
      )}
      {/* -------- TABLE TYPE: USER -------- */}
      {tableType === "user" && (
        <div className="settings-listing-table table-wrapper">
          <Table className="table-wrapper">
            <thead className="sticky-table-header">
              <tr className="table-header-tr sticky-table-header">
                {Object.keys(tableHeaders).map((key, index) => (
                  <th
                    key={index}
                    /*    className={`table-header__item ${key}`} */
                    id={`csl-${key}-header`}
                  >
                    <p className="table-header__title">{tableHeaders[key]}</p>
                    {/* If the key is "name" or "location", display the sort button */}
                    {key === "lastname" ||
                    key === "firstname" ||
                    key === "location" ||
                    key === "sentTime" ? (
                      <Button
                        variant="link"
                        className="table-header-sort__btn"
                        onClick={() => handleSort(key)}
                      >
                        {sortField === key && sortDirection === "asc" ? (
                          <SortDescendingOutlined />
                        ) : (
                          <SortAscendingOutlined />
                        )}
                      </Button>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {
                /* Display no data if the sortedData length is 0*/
                sortedData.length === 0 && (
                  <tr>
                    <td
                      colSpan={Object.keys(tableHeaders).length}
                      className="table-data"
                    >
                      {t("noDataToDisplay")}
                    </td>
                  </tr>
                )
              }
              {sortedData.map((data, index) => (
                <tr key={index}>
                  {Object.keys(tableHeaders).map((key, index) => (
                    <td
                      key={index}
                      className={`table-data ${key}`}
                      id={`csl-${key}-data`}
                    >
                      <div className="ellipsis">
                        {/* If the key is "moreInfo", display the edit button */}
                        {key === "userMoreInfo" ? (
                          <Button
                            variant="link"
                            onClick={() => {
                              handleUserInfoModalShow(data);
                            }}
                          >
                            <InfoCircleOutlined />
                          </Button>
                        ) : key === "notificationMoreInfo" ? (
                          <Button
                            variant="link"
                            onClick={handleNotificationInfoModalShow}
                          >
                            <InfoCircleOutlined />
                          </Button>
                        ) : key === "emailnotification" ? (
                          data[key] ? (
                            t("on")
                          ) : (
                            t("off")
                          )
                        ) : key === "roles" ? (
                          roleMapping[data[key]]
                        ) : (
                          data[key]
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>

          {/* ----------- USER MORE INFO MODAL ----------- */}
          <SettingsUserMoreInfoModal
            show={showUserInfoModal}
            onClose={handleUserInfoModalClose}
            userInfo={modalUserInfo}
            onEditUser={handleEditUser}
            roleMapping={roleMapping}
            t={t}
          />

          {/* ----------- NOTIFICATION MORE INFO MODAL ----------- */}
          {/* <Modal
            show={showNotificationInfoModal}
            onHide={handleNotificationInfoModalClose}
          >
            <Modal.Header closeButton>
              <Modal.Title>Értesítés részletei</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                <strong>Cím:</strong> {notificationsDummyDataHun[0].title}
              </p>
              <p>
                <strong>Üzenet:</strong> {notificationsDummyDataHun[0].message}
              </p>
              <p>
                <strong>Felhasználó:</strong>{" "}
                {notificationsDummyDataHun[0].receivedUser}
              </p>
              <p>
                <strong>Időpont:</strong>{" "}
                {notificationsDummyDataHun[0].sentTime}
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={handleNotificationInfoModalClose}
              >
                {t("close")}
              </Button>
            </Modal.Footer>
          </Modal> */}
        </div>
      )}
      {/* ----------- COMPANIES TABLE TYPE ----------- */}
      {tableType === "company" && (
        <div className="settings-listing-table table-wrapper">
          <Table>
            <thead className="sticky-table-header">
              <tr className="table-header-tr">
                {Object.keys(tableHeaders).map((key, index) => (
                  <th key={index} id={`csl-${key}-header`}>
                    <p className="table-header__title">{tableHeaders[key]}</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(!tableData || tableData.length === 0) && (
                <tr>
                  <td
                    colSpan={Object.keys(tableHeaders).length}
                    className="table-data"
                  >
                    {t("noDataToDisplay")}
                  </td>
                </tr>
              )}
              {tableData &&
                tableData.map((data, index) => (
                  <tr key={index}>
                    {Object.keys(tableHeaders).map((key, index) => (
                      <td key={index} id={`csl-${key}-data`}>
                        <div className="ellipsis">
                          {key === "companyMoreInfo" ? (
                            // Only render the button if data is valid
                            <Button
                              variant="link"
                              onClick={() => {
                                handleCompanyInfoModalShow(data);
                              }}
                            >
                              <InfoCircleOutlined />
                            </Button>
                          ) : key === "status" ? (
                            data[key] === true ? ( // Compare to the boolean `true`
                              <span className="status-active">
                                {t("active")}
                              </span>
                            ) : (
                              <span className="status-inactive">
                                {t("inactive")}
                              </span>
                            )
                          ) : data[key] !== null && data[key] !== undefined ? (
                            data[key]
                          ) : (
                            t("tooltipNoData")
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </Table>
          {/* ----------- COMPANY MORE INFO MODAL ----------- */}
          <SettingsCompanyMoreInfoModal
            show={showCompanyInfoModal}
            onClose={handleCompanyInfoModalClose}
            companyInfo={modalCompanyInfo}
            onEditCompany={handleEditCompany}
            t={t}
          />
        </div>
      )}
    </>
  );
};

export default SettingsListingTable;
